import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Modal } from "react-bootstrap";
import { get_return_order_detail, download_return_label, get_order_detail, download_return_invoice, return_admin_decision, return_mark_as_received } from "../../reduxData/user/userAction";
import { formatPrice } from "../../hooks/formatPrice";
import { formatDateTime } from "../../hooks/formatDateTime";
import { languageFields } from "../../hooks/LanguageFields";
import "../../css/order.css";
import TranslatedText from "../../hooks/TranslatedText";
import StatusTracker from "../../components/StatusTracker";


const { REACT_APP_IMAGE_URL, REACT_APP_REDIRECT_URL } = process.env;

const getFullImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith("http") || url.startsWith("https")
        ? url
        : `${REACT_APP_IMAGE_URL}${url}`;
};

const normalizeReturnDecision = (value) => String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

const adminDecisionOptions = [
    {
        value: "resolve-overdue",
        title: "Resolve overdue return",
        description: "Review the overdue case and record the appropriate evidence, information, or closure decision.",
        icon: "bi-shield-check",
        tone: "resolve",
        confirmationText: "Select the case outcome after reviewing the shipment evidence and seller receipt response.",
    },
];

const overdueDecisionTypes = [
    {
        value: "ACCEPT_EVIDENCE",
        title: "Accept evidence",
        description: "Accept the buyer's late or manually reviewed shipment evidence.",
        icon: "bi-file-earmark-check",
    },
    {
        value: "REQUEST_MORE_INFO",
        title: "Request more information",
        description: "Keep the case open and request additional details from the buyer or seller.",
        icon: "bi-chat-left-text",
    },
    {
        value: "CLOSE_CASE",
        title: "Close case",
        description: "Close the overdue case after completing the administrative review.",
        icon: "bi-x-circle",
    },
];

const ReturnDetail = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams(); // Return ID
    const [loading, setLoading] = useState(false);
    const [adminDecision, setAdminDecision] = useState(null);
    const [labelLoading, setLabelLoading] = useState(false);
    const [overdueDecisionType, setOverdueDecisionType] = useState("");
    const [overdueDecisionTypeError, setOverdueDecisionTypeError] = useState("");
    const [decisionReason, setDecisionReason] = useState("");
    const [decisionReasonError, setDecisionReasonError] = useState("");
    const [receiveConfirmation, setReceiveConfirmation] = useState(false);
    const { user } = useSelector((state) => ({ user: state.auth.auth }));
    const { return_order_detail, orderDetail, soldItemDetail } = useSelector((state) => state.user);
    // const currentOrderDetail = orderDetail?.[0] || orderDetail || soldItemDetail?.[0] || soldItemDetail || null;
    const currentOrderDetail = orderDetail?.[0] ?? soldItemDetail?.[0] ?? null;
    const returnData = return_order_detail || {};
    const orderData = returnData?.orderData || {};
    const buyerData = returnData?.buyerData || {};
    const returnItems = returnData?.return_items || [];
    const returnMethod = returnData?.return_method;
    const isOwnShipping = returnMethod === "OWN_SHIPPING";
    const shipmentProof = returnData?.proof_of_shipment_upload || returnData?.proof_of_shipment || [];
    const shipmentProofs = (Array.isArray(shipmentProof) ? shipmentProof : [shipmentProof]).filter(Boolean);
    const currentReturnStatus = String(returnData?.return_status || returnData?.status || "").toUpperCase();
    const rawAdminDecision = returnData?.admin_decision?.decision
        || returnData?.admin_decision_type
        || returnData?.decision
        || returnData?.resolution
        || currentReturnStatus;
    const adminDecisionStatus = normalizeReturnDecision(rawAdminDecision);
    const hasVerifiedShipmentEvidence = Boolean(returnData?.proof_of_shipment_verified_at);
    const isEvidenceAccepted = ["ACCEPT_EVIDENCE", "EVIDENCE_ACCEPTED"].includes(adminDecisionStatus)
        || (hasVerifiedShipmentEvidence && !returnData?.return_received_at);
    const isMoreInfoRequested = [
        "REQUEST_MORE_INFO",
        "MORE_INFO_REQUESTED",
        "RETURN_INFO_REQUESTED",
        "INFORMATION_REQUESTED",
    ].includes(adminDecisionStatus);
    const isCaseClosed = ["CLOSE_CASE", "CASE_CLOSED"].includes(adminDecisionStatus);
    const isReturnShipmentOverdue = currentReturnStatus === "RETURN_SHIPMENT_OVERDUE";
    const hasShipmentSubmission = Boolean(
        (returnData?.carrier || returnData?.carrier_name)
        && returnData?.shipped_at
        && (returnData?.tracking_number || shipmentProofs.length)
    );
    const availableOverdueDecisionTypes = overdueDecisionTypes.filter((option) => (
        hasShipmentSubmission
            ? option.value !== "REQUEST_MORE_INFO"
            : option.value !== "ACCEPT_EVIDENCE"
    ));
    const sellerReceiptConfirmed = returnData?.seller_receipt_response === true
        || returnData?.seller_receipt_response === 1
        || returnData?.seller_receipt_response === "1";
    const needsOverdueReview = !hasShipmentSubmission
        || (hasShipmentSubmission && !sellerReceiptConfirmed);
    const adminReviewDeadline = returnData?.return_ship_by_at || null;
    const adminReviewDeadlineTime = adminReviewDeadline ? new Date(adminReviewDeadline).getTime() : null;
    const hasExpiredAdminReviewDeadline = Number.isFinite(adminReviewDeadlineTime) && Date.now() > adminReviewDeadlineTime;
    const showOverdueAdminDecision = isOwnShipping
        && hasExpiredAdminReviewDeadline
        && isReturnShipmentOverdue
        && needsOverdueReview;
    const returnReceived = currentReturnStatus === "RETURN_RECEIVED" || Boolean(returnData?.return_received_at);
    const canMarkAsReceived = Boolean(returnData?.id) && !returnReceived && ![
        "CANCELLED",
        "FAILED",
        "REJECTED",
        "REFUNDED",
        "COMPLETED",
    ].includes(currentReturnStatus);
    const returnRefunded = ["REFUNDED", "COMPLETED"].includes(currentReturnStatus)
        || returnData?.refund_status === "REFUNDED"
        || returnData?.return_items?.some((item) => item?.is_refunded);
    const payoutReleased = currentReturnStatus === "PAYOUT_RELEASED"
        || String(returnData?.payout_status || "").toUpperCase() === "PAYOUT_RELEASED";
    const ownShippingSteps = [
        { key: "requested", label: "Return Requested", icon: "bi-arrow-return-left", complete: true },
        {
            key: "shipment",
            label: isMoreInfoRequested && !hasShipmentSubmission
                ? "More Information Requested"
                : isReturnShipmentOverdue && !hasShipmentSubmission ? "Shipment Overdue" : "Shipment Submitted",
            icon: isMoreInfoRequested && !hasShipmentSubmission
                ? "bi-chat-left-text"
                : isReturnShipmentOverdue && !hasShipmentSubmission ? "bi-exclamation-triangle" : "bi-truck",
            complete: hasShipmentSubmission,
        },
        {
            key: "received",
            label: isEvidenceAccepted ? "Evidence Accepted" : isCaseClosed ? "Admin Reviewed" : "Return Received",
            icon: isEvidenceAccepted ? "bi-file-earmark-check" : isCaseClosed ? "bi-shield-check" : "bi-box-seam",
            complete: returnReceived || isEvidenceAccepted || isCaseClosed,
        },
        {
            key: "final",
            label: isCaseClosed ? "Case Closed" : payoutReleased ? "Payout Released" : "Refunded",
            icon: isCaseClosed ? "bi-x-circle" : payoutReleased ? "bi-unlock" : "bi-cash-stack",
            complete: isCaseClosed || payoutReleased || returnRefunded,
        },
    ];
    const currentOwnShippingStep = (isCaseClosed || payoutReleased || returnRefunded) ? "final"
        : (returnReceived || isEvidenceAccepted) ? "received"
            : hasShipmentSubmission ? "received"
                : isReturnShipmentOverdue ? "shipment" : "requested";
    const selectedAdminDecision = adminDecisionOptions.find((option) => option.value === adminDecision);

    const closeAdminDecisionModal = () => {
        if (loading) return;
        setAdminDecision(null);
        setOverdueDecisionType("");
        setOverdueDecisionTypeError("");
        setDecisionReason("");
        setDecisionReasonError("");
    };

    // Fallback items from top level if return_items is empty
    const itemsToShow = returnItems.length > 0
        ? returnItems
        : returnData?.items || [];

    useEffect(() => {
        if (!id) return;
        const fetchData = async () => {
            await get_return_order_detail(dispatch, id);
        };
        fetchData();
    }, [dispatch]);

    useEffect(() => {
        if (!returnData?.order_id) return;
        const fetchOrderData = async () => {
            await get_order_detail(dispatch, returnData?.order_id);
        };
        fetchOrderData();
    }, [dispatch, returnData?.order_id]);

    const returnStatusLabel = (status) => {
        if (!status) return "";
        return status.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const handleContactEmail = () => {
        if (currentOrderDetail?.sellerdata?.email) {
            window.location.href = `mailto:${currentOrderDetail.sellerdata.email}`;
        }
    };

    const handleContactPhone = () => {
        const seller = currentOrderDetail?.sellerdata;
        if (seller?.phone_no) {
            window.location.href = `tel:${seller.country_code}${seller.phone_no}`;
        }
    };

    const isHiddenStatus = ["FAILED", "CANCELLED", "PENDING"].includes(returnData?.return_status);

    const returnTagClass = (status) => {
        const s = String(status || "").toUpperCase();
        if (s === "APPROVED" || s === "COMPLETED" || s === "REFUNDED" || s === "RETURN_RECEIVED") return "approved";
        if (s === "REJECTED") return "rejected";
        return "pending";
    };
    const handleDetail = (slug) => {
        if (slug) {
            window.open(
                `${REACT_APP_REDIRECT_URL}auction/${slug}`,
                "_blank",
                "noopener,noreferrer"
            );
        }
    }

    const calculateDisputedTotal = () => {
        if (!returnItems || returnItems.length === 0) return 0;
        return returnItems.reduce((total, item) => {
            const itemInfo = item?.itemData || {};
            const variantInfo = item?.variantData || {};
            const itemPrice = parseFloat(
                variantInfo?.bid_amount != 0
                    ? variantInfo.bid_amount
                    : itemInfo?.original_retail_price || 0
            );
            const quantity = parseInt(
                itemInfo?.quantity || item?.quantity || 1,
                10
            );
            return total + itemPrice * quantity;
        }, 0);
    };

    const disputedTotal = calculateDisputedTotal();

    const submitAdminDecision = async () => {
        const isAvailableDecisionType = availableOverdueDecisionTypes.some(
            (option) => option.value === overdueDecisionType
        );
        if (!overdueDecisionType || !isAvailableDecisionType) {
            setOverdueDecisionTypeError("Select a case decision.");
            return;
        }
        const reason = decisionReason.trim();
        if (!reason) {
            setDecisionReasonError("A decision reason is required for the audit log.");
            return;
        }
        const payload = {
            id: returnData?.id,
            action: adminDecision,
            decision: overdueDecisionType,
            reason: reason,
        };
        setLoading(true);
        const response = await return_admin_decision(dispatch, payload);
        if (response) {
            setAdminDecision(null);
            setOverdueDecisionType("");
            setOverdueDecisionTypeError("");
            setDecisionReason("");
            setDecisionReasonError("");
            await get_return_order_detail(dispatch, id);
        }
        setLoading(false);
    };

    const markReturnAsReceived = async () => {
        if (!returnData?.id) return;
        setLoading(true);
        const type = String(returnMethod || "").toUpperCase() === "BID4STYLE"
            ? "bid4style"
            : "OWN_SHIPPING";
        const response = await return_mark_as_received(dispatch, returnData.id, type);
        if (response) {
            setReceiveConfirmation(false);
            await get_return_order_detail(dispatch, id);
        }
        setLoading(false);
    };

    const downloadInvoice = async () => {
        try {
            setLoading(true);
            const payloud = {
                returnId: returnData?.id,
                role: "admin",
            }
            const response = await download_return_invoice(dispatch, payloud);
            const blob = response.data;
            if (!blob.size) throw new Error("Empty file");
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${t("OrderDetail.invoice")}-${returnData?.id || "download"}.pdf`;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };


    const downloadLabel = async () => {
        try {
            setLabelLoading(true);
            const response = await download_return_label(dispatch, returnData?.id);
            const blob = response.data;
            if (!blob?.size) throw new Error("Empty file");
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${t("returnOrder.returnlabel")}-${returnData?.id || "download"}.pdf`;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
        } catch (e) { console.error(e); }
        finally { setLabelLoading(false); }
    };

    return (
        <div className="od2-page container">
            <div className="od2-header">
                <div className="od2-header-left">
                    <div className="od2-order-title">
                        <h2 className="od2-order-title">
                            {t("returnOrder.ReturnDetails")}
                        </h2>
                        <span className={`od2-status-pill ${returnTagClass(returnData?.return_status)}`}>
                            <TranslatedText text={returnStatusLabel(returnData?.return_status)} />
                        </span>
                    </div>

                    <div className="od2-order-meta">
                        <span className="od2-meta-item">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                            {t("returnOrder.ReturnDate")}: {formatDateTime(returnData?.created_at, i18n)}
                        </span>
                        <span className="od2-meta-item">{t("returnOrder.OrderId")} #{currentOrderDetail?.order_id}</span>
                        <span className="od2-meta-item">{t("returnOrder.ReturnID")} #{returnData?.id}</span>
                    </div>
                </div>

                <div className="od2-header-actions">
                    {canMarkAsReceived && (
                        <button
                            type="button"
                            className="od2-btn-invoice"
                            onClick={() => setReceiveConfirmation(true)}
                            disabled={loading}
                        >
                            <i className="bi bi-box-seam"></i>
                            Mark as Received
                        </button>
                    )}
                    {!isHiddenStatus && (
                        <button className="od2-btn-invoice" onClick={downloadInvoice} disabled={loading}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                            {t("returnOrder.DownloadReturnInvoice")}{loading ? "…" : ""}
                        </button>
                    )}
                </div>
            </div>

            <div className="od2-layout">
                {/* Left Column - Returned Items */}
                <div className="od2-col-left">
                    <div className="od2-card">
                        <div className="od2-card-header">
                            <h4 className="od2-card-title">
                                {t("returnOrder.ReturnedItems") || "Returned Items"}
                            </h4>
                        </div>

                        <div className="od2-card-body">
                            {itemsToShow.length > 0 ? (
                                itemsToShow.map((item, index) => {
                                    const itemInfo = item?.itemData || {};
                                    const variantInfo = item?.variantData || {};
                                    const imageArray = variantInfo.images?.length
                                        ? variantInfo.images
                                        : itemInfo.ai_images || [];
                                    const imageUrl = getFullImageUrl(imageArray[0]);
                                    const title = languageFields(itemInfo, "title", i18n) || itemInfo.title || "Product Item";
                                    const itemPrice = variantInfo?.bid_amount != 0 ? variantInfo.bid_amount : itemInfo?.original_retail_price ?? null;
                                    return (
                                        <div
                                            key={index}
                                            className={`od2-item-row ${index !== itemsToShow.length - 1 ? "od2-item-row--divider" : ""}`}
                                        >
                                            <img
                                                className="od2-item-img"
                                                src={imageUrl}
                                                alt={title}
                                            />

                                            <div className="od2-item-body">
                                                <h5 className="od2-item-name">
                                                    {title}
                                                </h5>

                                                <div className="od2-item-attrs">
                                                    {variantInfo.size_name && (
                                                        <span className="od2-attr-chip">
                                                            {t("returnOrder.Size")} <strong>{variantInfo.size_name}</strong>
                                                        </span>
                                                    )}
                                                    {variantInfo.color_name && (
                                                        <span className="od2-attr-chip">
                                                            {t("returnOrder.Color")}:{" "}
                                                            <strong><TranslatedText text={variantInfo.color_name} /></strong>
                                                        </span>
                                                    )}
                                                    {itemPrice != null && (
                                                        <span className="od2-attr-chip od2-attr-chip--price">
                                                            {formatPrice(itemPrice, i18n)}
                                                        </span>
                                                    )}
                                                    {item.quantity && (
                                                        <span className="od2-attr-chip">
                                                            {t("returnOrder.Quantity")}: <strong>{item.quantity}</strong>
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="od2-actions">
                                                    <button className="od2-btn-detail" onClick={() => handleDetail(itemInfo?.slug)}>
                                                        {t("returnOrder.MoreDetails")}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="od2-summary-value">No items found in this return.</p>
                            )}
                        </div>
                    </div>
                    <div className="od2-card">
                        <h4 className="od2-card-title mb-0">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
                            {t("returnOrder.OrderSummary")}
                        </h4>
                        <div className="od2-summary-rows">
                            <div className="od2-summary-row">
                                <span className="od2-summary-label">{t("returnOrder.Subtotal")}</span>
                                <span className="od2-summary-value">{formatPrice(disputedTotal, i18n)}</span>
                            </div>
                            <div className="od2-summary-row total pb-0">
                                <span className="od2-summary-label">{t("returnOrder.Total")}</span>
                                <span className="od2-summary-value">{formatPrice(disputedTotal, i18n)}</span>
                            </div>
                        </div>
                    </div>
                    {!isHiddenStatus && !isOwnShipping && (
                        <div className="od2-card">
                            <div className="od2-track-header">
                                <h4 className="od2-card-title" style={{ margin: 0, border: "none", paddingBottom: 0 }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                    {t("returnOrder.TrackYourOrder")}
                                </h4>
                            </div>
                            <StatusTracker
                                isReturn={true}
                                isRefunded={returnData?.return_items?.[0].is_refunded}
                                status={returnData?.return_status}
                            />
                            <div style={{ marginTop: 20 }}>
                                <button className="od2-btn-label" onClick={downloadLabel} disabled={labelLoading}>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                                    {t("returnOrder.DownloadReturnLabel")} {labelLoading && '...'}
                                </button>
                            </div>
                        </div>
                    )}

                    {isOwnShipping && (
                        <div className="od2-card admin-own-return-flow-card">
                            <div className="admin-own-return-flow-header">
                                <div>
                                    <span className="admin-own-return-flow-eyebrow">Own shipping</span>
                                    <h4><i className="bi bi-truck me-2"></i>Return shipment progress</h4>
                                </div>
                                <span className={`admin-own-return-flow-status ${isReturnShipmentOverdue ? "overdue" : ""}`}>
                                    {isCaseClosed
                                        ? "Case Closed"
                                        : isEvidenceAccepted
                                            ? "Evidence Accepted"
                                            : isMoreInfoRequested && !hasShipmentSubmission
                                                ? "More Information Requested"
                                                : isReturnShipmentOverdue
                                                    ? "Action required"
                                                    : returnStatusLabel(currentReturnStatus)}
                                </span>
                            </div>
                            <div className="admin-own-return-flow">
                                {ownShippingSteps.map((step, index) => {
                                    const isCurrent = step.key === currentOwnShippingStep;
                                    const isOverdue = isReturnShipmentOverdue && (
                                        (step.key === "shipment" && !hasShipmentSubmission)
                                        || (step.key === "received" && hasShipmentSubmission && !sellerReceiptConfirmed)
                                    );
                                    return (
                                        <div className={`admin-own-return-step ${step.complete ? "complete" : ""} ${isCurrent ? "current" : ""} ${isOverdue ? "overdue" : ""}`} key={step.key}>
                                            <div className="admin-own-return-step-marker">
                                                <i className={`bi ${step.complete ? "bi-check-lg" : step.icon}`}></i>
                                            </div>
                                            <div className="admin-own-return-step-copy">
                                                <strong>{step.label}</strong>
                                                <small>
                                                    {step.key === "requested" && formatDateTime(returnData?.created_at, i18n)}
                                                    {step.key === "shipment" && (returnData?.shipped_at
                                                        ? formatDateTime(returnData.shipped_at, i18n)
                                                        : returnData?.return_ship_by_at
                                                            ? `Due ${formatDateTime(returnData.return_ship_by_at, i18n)}`
                                                            : "Awaiting buyer shipment")}
                                                    {step.key === "received" && (returnData?.return_received_at
                                                        ? formatDateTime(returnData.return_received_at, i18n)
                                                        : isEvidenceAccepted
                                                            ? "Shipment evidence accepted by admin"
                                                            : isCaseClosed
                                                                ? "Administrative review completed"
                                                                : returnData?.return_ship_by_at
                                                                    ? `Due ${formatDateTime(returnData.return_ship_by_at, i18n)}`
                                                                    : "Awaiting seller receipt")}
                                                    {step.key === "final" && (isCaseClosed
                                                        ? "Closed without buyer refund"
                                                        : payoutReleased
                                                            ? (returnData?.payout_released_at ? formatDateTime(returnData.payout_released_at, i18n) : "Seller payout hold released")
                                                            : (returnData?.refunded_at
                                                                ? formatDateTime(returnData.refunded_at, i18n)
                                                                : returnRefunded && returnData?.updated_at
                                                                    ? formatDateTime(returnData.updated_at, i18n)
                                                                    : "Pending return completion"))}
                                                </small>
                                            </div>
                                            {index < ownShippingSteps.length - 1 && <span className="admin-own-return-step-line" />}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {showOverdueAdminDecision && (
                        <section className="admin-return-decision-panel">
                            <div className="admin-return-decision-heading">
                                <span className="admin-return-decision-shield"><i className="bi bi-shield-exclamation"></i></span>
                                <div>
                                    <span className="admin-return-decision-eyebrow">Admin action required</span>
                                    <h4>Own-shipping return is overdue</h4>
                                    <p>{hasShipmentSubmission
                                        ? "The buyer submitted shipment evidence, but the seller did not confirm receipt before the case became overdue."
                                        : "The shipping deadline passed without a carrier scan or accepted proof."} The payout remains blocked and the refund remains withheld until an administrator records a decision.</p>
                                </div>
                            </div>
                            <div className="admin-return-deadline">
                                <span><i className="bi bi-calendar-x me-2"></i>{hasShipmentSubmission ? "Seller receipt deadline" : "Return ship-by deadline"}</span>
                                <strong>{formatDateTime(adminReviewDeadline, i18n)}</strong>
                            </div>
                            <div className="admin-return-decision-actions">
                                {adminDecisionOptions.map((option) => (
                                    <button
                                        type="button"
                                        className={`admin-return-decision-card ${option.tone}`}
                                        key={option.value}
                                        onClick={() => {
                                            setAdminDecision(option.value);
                                            setOverdueDecisionType("");
                                            setOverdueDecisionTypeError("");
                                            setDecisionReason("");
                                            setDecisionReasonError("");
                                        }}
                                    >
                                        <span className="admin-return-decision-card-icon"><i className={`bi ${option.icon}`}></i></span>
                                        <span className="admin-return-decision-card-copy">
                                            <strong>{option.title}</strong>
                                            <small>{option.description}</small>
                                        </span>
                                        <i className="bi bi-chevron-right admin-return-decision-arrow"></i>
                                    </button>
                                ))}
                            </div>
                        </section>
                    )}
                </div>

                {/* Right Column - Party & Order Info */}
                <div className="od2-col-right">
                    {currentOrderDetail?.sellerdata && (
                        <div className="od2-card">
                            <h4 className="od2-card-title">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
                                {t("returnOrder.SellerDetails")}
                            </h4>
                            {currentOrderDetail.sellerdata.company_logo && (
                                <img
                                    className="od2-seller-logo"
                                    src={`${REACT_APP_IMAGE_URL}${currentOrderDetail.sellerdata.company_logo}`}
                                    alt="Seller logo"
                                />
                            )}
                            <p className="od2-seller-name">{currentOrderDetail.sellerdata.store_name}</p>
                            <ul className="od2-contact-list">
                                <li className="od2-contact-item">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                                    {currentOrderDetail.sellerdata.user_name}
                                </li>
                                {currentOrderDetail.sellerdata.location && (
                                    <li className="od2-contact-item">
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                                        {currentOrderDetail.sellerdata.location}
                                    </li>
                                )}
                                <li className="od2-contact-item">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                                    <a href={`mailto:${currentOrderDetail.sellerdata.email}`}>{currentOrderDetail.sellerdata.email}</a>
                                </li>
                                {currentOrderDetail.sellerdata.phone_no && (
                                    <li className="od2-contact-item clickable" onClick={handleContactPhone}>
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" /></svg>
                                        {currentOrderDetail.sellerdata.country_code} {currentOrderDetail.sellerdata.phone_no}
                                    </li>
                                )}
                            </ul>
                        </div>
                    )}

                    <div className="od2-card">
                        <h4 className="od2-card-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                            {t("returnOrder.Information")}
                        </h4>
                        <div className="od2-info-list">
                            <div className="od2-info-item">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                                <span>{currentOrderDetail?.user_name}</span>
                            </div>
                            <div className="od2-info-item">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                                <span>{currentOrderDetail?.user_email}</span>
                            </div>
                            <div className="od2-info-item">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" /></svg>
                                <span>{currentOrderDetail?.user_country_code} {currentOrderDetail?.user_phone_no}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Return Information Card */}
            <div className="od2-return-card">
                <div className="od2-return-header">
                    <h2 className="od2-return-title">{t("returnOrder.ReturnInformation") || "Return Information"}</h2>
                    <span className={`od2-return-status-badge ${returnTagClass(returnData?.return_status)}`}>
                        <TranslatedText text={returnStatusLabel(returnData?.return_status)} />
                    </span>
                </div>

                <div className="od2-return-meta-grid">
                    <div className="od2-return-meta-box">
                        <div className="od2-return-meta-label">{t("returnOrder.ReturnID")}</div>
                        <p className="od2-return-meta-value">#{returnData?.id}</p>
                    </div>
                    <div className="od2-return-meta-box">
                        <div className="od2-return-meta-label">{t("returnOrder.ReturnDate")}</div>
                        <p className="od2-return-meta-value">
                            {formatDateTime(returnData?.created_at, i18n)}
                        </p>
                    </div>
                    {/* <div className="od2-return-meta-box">
                        <div className="od2-return-meta-label">{t("OrderDetail.PayoutMethod")}</div>
                        <p className="od2-return-meta-value">{returnData?.payment_method?.toUpperCase() || "—"}</p>
                    </div> */}

                    <div className="od2-return-meta-box">
                        <div className="od2-return-meta-label">{t("returnOrder.PayoutMethod")}</div>
                        <p className="od2-return-meta-value">
                            {return_order_detail?.is_credit_point
                                ? t("returnOrder.StoreCreditPoints")
                                : t("returnOrder.RefundAmount")}
                        </p>
                    </div>

                </div>

                <div className="od2-return-meta-grid admin-return-method-grid">
                    <div className="od2-return-meta-box">
                        <div className="od2-return-meta-label">Return Method</div>
                        <p className="od2-return-meta-value">
                            {returnMethod ? returnMethod.replaceAll("_", " ") : "—"}
                        </p>
                    </div>

                    {isOwnShipping && (
                        <>
                            <div className="od2-return-meta-box">
                                <div className="od2-return-meta-label">Carrier Name</div>
                                <p className="od2-return-meta-value">{returnData?.carrier || returnData?.carrier_name || "—"}</p>
                            </div>
                            <div className="od2-return-meta-box">
                                <div className="od2-return-meta-label">Tracking Number</div>
                                <p className="od2-return-meta-value">{returnData?.tracking_number || "—"}</p>
                            </div>
                            <div className="od2-return-meta-box">
                                <div className="od2-return-meta-label">Shipped Date</div>
                                <p className="od2-return-meta-value">
                                    {returnData?.shipped_at ? formatDateTime(returnData.shipped_at, i18n) : "—"}
                                </p>
                            </div>
                            <div className="od2-return-meta-box admin-return-proof-box">
                                <div className="od2-return-meta-label">Shipment Proof</div>
                                {shipmentProofs.length > 0 ? (
                                    <div className="admin-return-proof-list">
                                        {shipmentProofs.map((proof, index) => {
                                            const proofPath = typeof proof === "string" ? proof : proof?.url || proof?.path;
                                            return proofPath ? (
                                                <button
                                                    type="button"
                                                    className="admin-return-proof-button"
                                                    key={`${proofPath}-${index}`}
                                                    onClick={() => window.open(getFullImageUrl(proofPath), "_blank", "noopener,noreferrer")}
                                                >
                                                    <img src={getFullImageUrl(proofPath)} alt={`Shipment proof ${index + 1}`} />
                                                </button>
                                            ) : null;
                                        })}
                                    </div>
                                ) : (
                                    <p className="od2-return-meta-value">—</p>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {returnData?.reason != "other" && (
                    <div className="od2-return-section">
                        <div className="od2-return-section-label">{t("returnOrder.ReturnReason")}</div>
                        <p className="od2-dispute-reason">
                            <TranslatedText text={returnData?.reason} />
                        </p>
                    </div>
                )}

                {returnData?.comment && (
                    <div className="od2-return-section">
                        <div className="od2-return-section-label">{t("returnOrder.ReturnReason")}</div>
                        <p className="od2-dispute-reason"> <TranslatedText text={returnData?.comment} /></p>
                    </div>
                )}

                <div className="od2-return-highlight-row">
                    <div className="od2-highlight-box">
                        <div className="od2-highlight-label">{t("returnOrder.UnusedItem")}</div>
                        <p className="od2-highlight-value">
                            {return_order_detail?.confirmunused ? t("returnOrder.Yes") : t("returnOrder.No")}
                        </p>
                    </div>
                    {returnData?.is_credit_point ?
                        <div className="od2-highlight-box">
                            <div className="od2-highlight-label">{t("returnOrder.StoreCreditPoints")}</div>
                            <p className="od2-highlight-value">
                                {returnData.refund_credits} {t("returnOrder.Points")}
                            </p>
                        </div> :
                        <div className="od2-highlight-box">
                            <div className="od2-highlight-label">{t("returnOrder.RefundAmount")}</div>
                            <p className="od2-highlight-value">
                                {formatPrice(returnData?.refund_amount, i18n)}
                            </p>
                        </div>
                    }
                </div>

                {returnData?.images?.length > 0 && (
                    <div className="od2-return-section">
                        <div className="od2-return-section-label">{t("returnOrder.AttachedImages")} </div>
                        <div className="od2-return-images">
                            {returnData.images.map((img, i) => (
                                <img key={i} className="od2-return-img" src={getFullImageUrl(img)} alt="Proof" />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <Modal
                show={Boolean(adminDecision)}
                onHide={() => { }}
                centered
                scrollable
                backdrop="static"
                keyboard={false}
                className="admin-return-confirm-modal"
            >
                <Modal.Header className="admin-return-confirm-header">
                    <div className="admin-return-confirm-title">
                        <span className="admin-return-confirm-title-icon"><i className="bi bi-shield-check"></i></span>
                        <div>
                            <span>Administrator decision</span>
                            <Modal.Title>{selectedAdminDecision?.title}</Modal.Title>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="admin-return-modal-close-btn"
                        aria-label="Close decision modal"
                        disabled={loading}
                        onClick={closeAdminDecisionModal}
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                </Modal.Header>
                <Modal.Body className="admin-return-confirm-body">
                    <div className="admin-return-confirm-warning">
                        <i className="bi bi-exclamation-triangle"></i>
                        <p>{selectedAdminDecision?.confirmationText} The mandatory reason will be stored in the administrative audit log.</p>
                    </div>
                    <fieldset className="admin-overdue-decision-fieldset">
                        <legend>Case decision <span>*</span></legend>
                        <div className="admin-overdue-decision-types">
                            {availableOverdueDecisionTypes.map((option) => (
                                <button
                                    type="button"
                                    key={option.value}
                                    className={`admin-overdue-decision-type ${overdueDecisionType === option.value ? "selected" : ""}`}
                                    onClick={() => {
                                        setOverdueDecisionType(option.value);
                                        setOverdueDecisionTypeError("");
                                    }}
                                >
                                    <span className="admin-overdue-decision-type-icon"><i className={`bi ${option.icon}`}></i></span>
                                    <span><strong>{option.title}</strong><small>{option.description}</small></span>
                                    <i className={`bi ${overdueDecisionType === option.value ? "bi-check-circle-fill" : "bi-circle"}`}></i>
                                </button>
                            ))}
                        </div>
                        {overdueDecisionTypeError && <div className="admin-return-reason-error">{overdueDecisionTypeError}</div>}
                    </fieldset>
                    <label className="admin-return-reason-label" htmlFor="returnAdminDecisionReason">
                        Decision reason <span>*</span>
                    </label>
                    <textarea
                        id="returnAdminDecisionReason"
                        className={`admin-return-reason-input ${decisionReasonError ? "is-invalid" : ""}`}
                        rows="4"
                        value={decisionReason}
                        onChange={(event) => {
                            setDecisionReason(event.target.value);
                            if (event.target.value.trim()) setDecisionReasonError("");
                        }}
                        placeholder="Describe the evidence reviewed and explain why this case decision is appropriate..."
                    />
                    {decisionReasonError && <div className="admin-return-reason-error">{decisionReasonError}</div>}
                    <div className="admin-return-decision-summary">
                        <span>Return ID <strong>#{returnData?.id}</strong></span>
                        <span>Decision <strong>{overdueDecisionType || "Not selected"}</strong></span>
                    </div>
                </Modal.Body>
                <Modal.Footer className="admin-return-confirm-footer">
                    <button type="button" className="admin-return-cancel-btn" disabled={loading} onClick={closeAdminDecisionModal}>Cancel</button>
                    <button type="button" className="admin-return-submit-btn" disabled={loading} onClick={submitAdminDecision}>
                        <i className="bi bi-check2-circle me-2"></i>{loading ? "Submitting..." : "Confirm decision"}
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={receiveConfirmation}
                onHide={() => {
                    if (!loading) setReceiveConfirmation(false);
                }}
                centered
                backdrop="static"
                keyboard={!loading}
            >
                <Modal.Header closeButton={!loading}>
                    <Modal.Title>Mark Return as Received</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to mark return #{returnData?.id} as received?
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="model-no-btn"
                        disabled={loading}
                        onClick={() => setReceiveConfirmation(false)}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="model-yes-btn"
                        disabled={loading}
                        onClick={markReturnAsReceived}
                    >
                        {loading ? "Confirming..." : "Confirm"}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ReturnDetail;
